// AddViewCardModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import {
  getNodeTypeLabelFromDefinition,
  getNodeTypePluralLabelFromDefinition,
} from 'utilities/NodeTypeLabels';
import {
  buildViewDisplayTitle,
  getViewRowKey,
  getViewRowNodeTypeKey,
  getViewRowTitle,
  isReusableCreatedView,
  isCatchAllTableView,
} from 'utilities/ViewRows';

// Services
import { subscribeToViews } from 'services/views/ViewReadService';

// CSS
import './AddViewCardModal.css';

// Components
import AddViewCardModalOption from './option/AddViewCardModalOption';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import Tabs from 'components/tabs/Tabs';
import ViewModal from 'views/modal/ViewModal';
import { buildViewOptions } from 'views/modal/viewModalViewTypes';

const isCatchAllDatabaseView = (view) => isCatchAllTableView(view);
const TAB_EXISTING = 'EXISTING';
const TAB_CREATE = 'CREATE';

/**
 * AddViewCardModal
 * 
 * Modal for adding an existing view to a dashboard card. Shows a dropdown 
 * of compatible views that are not already on the dashboard.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when the modal should be closed
 * @param {array} existingViewIds - Array of view IDs that are already on the dashboard (to exclude from options)
 * @param {function} onAdd - Callback when a view is selected to be added, receives { viewId, titleOverride }
 * @param {string} [title] - Optional modal title override.
 * @param {string} [existingEmptyStateSubtitle] - Optional subtitle override for the empty existing-view state.
 * @param {string} [mode='GLOBAL'] - Dashboard view picker mode.
 * @param {array|null} allowedTypeKeys - Optional array of allowed node type keys to filter views by
 * @param {'DASHBOARD'|'NODE'|'DATABASE'|'PAGE'|'LAYOUT'} [createdFrom='DASHBOARD'] - Source context when creating a new view.
 * @param {string|null} dashboardKey - Dashboard key used to isolate dashboard-created views.
 * 
 * @returns {ReactNode} The AddViewCardModal component.
 */
const AddViewCardModal = React.memo(({
  isOpen,
  onClose,
  existingViewIds = [],
  onAdd,
  title = '',
  existingEmptyStateSubtitle = '',
  mode = 'GLOBAL',
  allowedTypeKeys = null,
  createdFrom = 'DASHBOARD',
  dashboardKey = null,
}) => {

  // ----- CONTEXT ----- //

  // Base Context

  const { activeBase } = useBase();

  // Node Types Context
  const { getAll } = useNodeTypes();
  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- STATE ----- //

  const [selectedTypeKey, setSelectedTypeKey] = useState('');
  const [createViewOpen, setCreateViewOpen] = useState(false);
  const [selectedTabKey, setSelectedTabKey] = useState(TAB_EXISTING);
  const [views, setViews] = useState([]);
  const modalTitle = String(title || '').trim() || 'Add View';
  const resolvedExistingEmptyStateSubtitle = String(existingEmptyStateSubtitle || '').trim()
    || 'Switch to Create View to make one for this dashboard.';

  // ----- MEMOS ----- //

  const allowedTypeSet = useMemo(() => {
    if (!Array.isArray(allowedTypeKeys)) return null;
    const cleaned = allowedTypeKeys
      .map((key) => String(key || '').trim())
      .filter(Boolean);
    if (!cleaned.length) return null;
    return new Set(cleaned);
  }, [allowedTypeKeys]);

  const viewTypeMeta = useMemo(() => {
    const map = {};
    buildViewOptions(true).forEach((option) => {
      // Keep the first TIMELINE option (Timeline) for type-keyed lookups; Gantt shares the same type key.
      if (map[option.key]) return;
      map[option.key] = {
        iconKey: option.iconKey,
        label: option.label,
      };
    });
    return map;
  }, []);

  const allNodeTypes = useMemo(() => {
    const source = getAll();
    return Array.isArray(source) ? source : [];
  }, [getAll]);

  const nodeTypeByKey = useMemo(() => {
    const map = new Map();
    allNodeTypes.forEach((typeDef) => {
      const key = String(typeDef?.key || '').trim();
      if (!key) return;
      map.set(key, typeDef);
    });
    return map;
  }, [allNodeTypes]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!activeBase?.key) {
      setViews([]);
      return undefined;
    }

    const unsubscribe = subscribeToViews(
      { baseKey: activeBase.key },
      (rows) => {
        setViews(Array.isArray(rows) ? rows : []);
      }
    );

    return () => unsubscribe && unsubscribe();
  }, [activeBase?.key, isOpen]);

  // ----- MEMOS ----- //

  const optionRows = useMemo(() => {
    const existingSet = new Set(Array.isArray(existingViewIds) ? existingViewIds : []);

    return (views || [])
      .filter((view) => {
        const viewKey = getViewRowKey(view);
        if (!viewKey || existingSet.has(viewKey)) return false;

        const scope = String(view?.scope || '').trim().toUpperCase();
        if (scope && scope !== 'NODE_TYPE') return false;

        // Dashboard-created views are intentionally isolated and not reusable in add-existing lists.
        if (!isReusableCreatedView(view)) return false;

        // Database "Database / View All" views are utility views for collection pages,
        // and are too broad/noisy for top-level global dashboards.
        if (isCatchAllDatabaseView(view)) return false;

        if (allowedTypeSet) {
          const nodeTypeKey = getViewRowNodeTypeKey(view);
          if (!nodeTypeKey || !allowedTypeSet.has(nodeTypeKey)) return false;
        }

        return true;
      })
      .map((view) => {
        const viewKey = getViewRowKey(view);
        const viewTitle = getViewRowTitle(view);
        const nodeTypeKey = getViewRowNodeTypeKey(view);
        const typeDef = nodeTypeByKey.get(nodeTypeKey) || null;
        const typeLabel = getNodeTypePluralLabelFromDefinition(typeDef, nodeTypeKey, { fallback: 'Items' });

        const viewTypeKey = String(view?.type || '').trim().toUpperCase();
        const typeMeta = viewTypeMeta[viewTypeKey] || {};
        const displayTitle = buildViewDisplayTitle({
          viewTitle,
          typeLabel,
        });
        const rowNoteParts = [typeMeta.label || '', typeLabel || ''];

        return {
          key: viewKey,
          iconKey: typeMeta.iconKey || '',
          note: rowNoteParts.filter(Boolean).join(' - '),
          cardTitle: displayTitle,
          nodeTypeKey,
          title: displayTitle,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [allowedTypeSet, existingViewIds, nodeTypeByKey, views, viewTypeMeta]);

  const typeOptions = useMemo(() => {
    return allNodeTypes
      .map((typeDef) => {
        const key = String(typeDef?.key || '').trim();
        if (!key) return null;
        if (typeDef?.system === true || typeDef?.isSystem === true) return null;
        if (allowedTypeSet && !allowedTypeSet.has(key)) return null;
        const label = getNodeTypeLabelFromDefinition(typeDef, key);
        const pluralLabel = getNodeTypePluralLabelFromDefinition(typeDef, key, { fallback: 'Items' });
        const description = String(typeDef?.description || '').trim();
        const iconKey = String(typeDef?.iconKey || '').trim() || 'deployed_code';
        return {
          key,
          label,
          iconKey,
          note: description || `Create a view for ${pluralLabel}.`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allNodeTypes, allowedTypeSet]);

  const tabs = useMemo(() => ([
    {
      key: TAB_EXISTING,
      label: 'Existing View',
      onClick: () => setSelectedTabKey(TAB_EXISTING),
    },
    {
      key: TAB_CREATE,
      label: 'Create View',
      onClick: () => setSelectedTabKey(TAB_CREATE),
    },
  ]), []);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTypeKey('');
    setSelectedTabKey(TAB_EXISTING);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setCreateViewOpen(false);
      return;
    }

    if (selectedTypeKey && !typeOptions.some((option) => option.key === selectedTypeKey)) {
      setSelectedTypeKey('');
    }
  }, [isOpen, selectedTypeKey, typeOptions]);

  // ----- HANDLERS ----- //

  /**
   * handleClose
   *
   * Closes the current surface.
   */
  const handleClose = () => {
    setCreateViewOpen(false);
    onClose && onClose();
  };

  /**
   * handleAdd
   *
   * Handles add.
   *
   * @param {unknown} viewOption - viewOption.
   */
  const handleAdd = (viewOption) => {
    if (!viewOption?.key) return;
    onAdd && onAdd({
      relatedTypeKey: viewOption.nodeTypeKey || null,
      titleOverride: viewOption.cardTitle || viewOption.label,
      typeKey: viewOption.nodeTypeKey || null,
      viewId: viewOption.key,
    });
    handleClose();
  };

  /**
   * handleCreateView
   *
   * Handles create view.
   *
   * @param {string} typeKey - typeKey.
   */
  const handleCreateView = (typeKey) => {
    if (!typeKey) return;
    setSelectedTypeKey(typeKey);
    setCreateViewOpen(true);
  };

  /**
   * handleCreateViewSave
   *
   * Handles create view save.
   *
   * @param {unknown} savedView - savedView.
   */
  const handleCreateViewSave = (savedView) => {
    const createdViewId = getViewRowKey(savedView);
    const createdTypeKey = getViewRowNodeTypeKey(savedView);
    if (!createdViewId) return;

    onAdd && onAdd({
      relatedTypeKey: createdTypeKey || null,
      viewId: createdViewId,
      typeKey: createdTypeKey || null,
      titleOverride: String(savedView?.title || '').trim() || null,
    });

    handleClose();
  };

  // ----- RENDER ----- //

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      width={isMobile ? '100%' : 520}
      height="auto"
      onClose={handleClose}
    >
      <ModalForm
        gap="10px"
      >

        {/* TABS */}
        <div className="add-view-card-modal-tabs">
          <Tabs
            tabs={tabs}
            selectedKey={selectedTabKey}
          />
        </div>

        {/* PANEL */}
        <div
          className="add-view-card-modal-panel"
          style={{
            borderColor: theme.status,
          }}
        >
          {selectedTabKey === TAB_EXISTING && (
            <>
              {optionRows.length === 0 && (
                <div
                  className="add-view-card-modal-empty-state"
                  style={{
                    borderColor: theme.status,
                  }}
                >
                  <div>No saved views available yet.</div>

                  {/* EMPTY STATE SUBTITLE */}
                  <div className="add-view-card-modal-empty-state-subtitle">
                    {resolvedExistingEmptyStateSubtitle}
                  </div>
                </div>
              )}

              {/* LIST */}
              {optionRows.length > 0 && (

                <div className="add-view-card-modal-list">
                  {optionRows.map((row) => (
                    <AddViewCardModalOption
                      key={row.key}
                      iconKey={row.iconKey}
                      title={row.title}
                      note={row.note}
                      onClick={() => handleAdd(row)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {selectedTabKey === TAB_CREATE && (
            <>

              {typeOptions.length === 0 && (
                <div
                  className="add-view-card-modal-empty-state"
                  style={{
                    borderColor: theme.status,
                  }}
                >
                  <div>No types available.</div>

                  {/* EMPTY STATE SUBTITLE */}
                  <div className="add-view-card-modal-empty-state-subtitle">
                    Add a node type before creating a new view.
                  </div>
                </div>
              )}

              {/* LIST */}
              {typeOptions.length > 0 && (

                <div className="add-view-card-modal-list">
                  {typeOptions.map((option) => (
                    <AddViewCardModalOption
                      key={option.key}
                      iconKey={option.iconKey}
                      title={option.label}
                      note={option.note}
                      onClick={() => handleCreateView(option.key)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

      </ModalForm>

      <ViewModal
        isOpen={createViewOpen}
        setOpen={setCreateViewOpen}
        typeKey={selectedTypeKey}
        baseKey={activeBase?.key || null}
        createdFrom={createdFrom}
        dashboardKey={dashboardKey || null}
        mode="create"
        onSave={handleCreateViewSave}
      />
    </Modal>
  );
});

// Dev Tools
AddViewCardModal.displayName = 'AddViewCardModal';

export default AddViewCardModal;
